import type { Severity, SectionId } from '../core/types';

/**
 * Presentation mappings shared across the UI.
 *
 * These live outside any component for two reasons. They are pure data, so a
 * component file exporting them defeats React Fast Refresh; and the severity
 * labels were previously duplicated between the badges and the Markdown export,
 * which is exactly how a rename ends up applied in one place and not the other.
 */

export const SEVERITY_LABEL: Record<Severity, string> = {
  critical: 'Critical',
  important: 'Important',
  polish: 'Polish',
};

/**
 * Score colour bands, matched to the grade thresholds in `core/scoring.ts`.
 *
 * Colour is a redundant signal here, never the only one: the number and the
 * letter grade are both on screen, so nothing depends on telling amber from red.
 *
 * Note the three bands that reach past `600` in light mode. Green, amber and red
 * are the lightest hues in the palette, and this `text` half is rendered as 14px
 * semibold — body text, so it owes 4.5:1. All three fall short of that on
 * `--ui-sunken`, which is the surface a `SectionBar` row takes on hover, so each
 * steps one further down the ramp. The vivid `stroke` half is unaffected; see the
 * note on rule 2 in `src/styles/tokens.css`.
 */
export function scoreTone(score: number): { stroke: string; text: string } {
  if (score >= 90) {
    return { stroke: 'var(--color-success-500)', text: 'text-success-700 dark:text-success-400' };
  }
  if (score >= 75) {
    return { stroke: 'var(--color-accent-500)', text: 'text-accent-600 dark:text-accent-400' };
  }
  if (score >= 60) {
    return { stroke: 'var(--color-brand-500)', text: 'text-brand-600 dark:text-brand-400' };
  }
  if (score >= 45) {
    return {
      stroke: 'var(--color-important-500)',
      text: 'text-important-700 dark:text-important-400',
    };
  }
  return { stroke: 'var(--color-critical-500)', text: 'text-critical-700 dark:text-critical-400' };
}

/**
 * The tint the section navigation gives to whichever section you are reading.
 *
 * This is **identity, not quality** — the opposite of `scoreTone` above. A
 * section keeps its hue whether it scores 12 or 98, so the nav never becomes a
 * second, competing severity signal. Encoding both in one colour is how a legend
 * ends up needing a legend.
 *
 * The hue is deliberately spent on *one* pill rather than given to all five as
 * permanent dots. Five identity colours sitting beside five score colours in a
 * 44px strip is ten colour signals fighting in one bar; tinting only the current
 * pill means the header quietly changes hue as you move down the form, which
 * says "you are somewhere new" without a single pixel of motion.
 *
 * It is also never the only signal: the active pill carries `aria-current`, its
 * label is text, and its score is a numeral. Colour only makes a pill you
 * already understand faster to find — so these pairs are still held to the
 * strict 4.5:1, and `scripts/contrast-audit.mjs` checks all ten.
 */
export function sectionHue(section: SectionId): string {
  switch (section) {
    case 'headline':
      return 'bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-100';
    case 'about':
      return 'bg-hype-50 text-hype-700 dark:bg-hype-950 dark:text-hype-100';
    case 'experience':
      return 'bg-accent-50 text-accent-700 dark:bg-accent-950 dark:text-accent-100';
    case 'skills':
      return 'bg-success-50 text-success-700 dark:bg-success-950 dark:text-success-100';
    case 'completeness':
      return 'bg-polish-50 text-polish-700 dark:bg-polish-950 dark:text-polish-100';
  }
}
